/**
 * PostHog feature-flag registry.
 *
 * Add a flag here and it becomes available as `useFeatureFlags().<key>` — no other wiring
 * needed.
 *
 * One PostHog project serves every environment (VITE_PUBLIC_POSTHOG_KEY lives in the shared
 * .env), so each flag has two keys in PostHog — "<name>_staging" and "<name>_production" — and
 * the suffix is resolved from VITE_ENV at read time. Register the base name only.
 */
export const FEATURE_FLAGS = {
    // Deliberately NOT the webapp's 'use_quote_attachments': QM-3816 wants the web store switched
    // separately from the admin app, and both apps share this PostHog project.
    showQuoteAttachments: 'use_webstore_quote_attachments',
    // Not the webapp's 'use_generated_documents': that one is turned on per organisation, while
    // the web store is switched on once for every customer.
    showGeneratedDocuments: 'use_webstore_generated_documents',
}

/**
 * getFlagEnvironment - The flag-name suffix for the current build.
 *
 * A function rather than a module-level constant so `vi.stubEnv('VITE_ENV', ...)` works in
 * tests — a constant would freeze the value at import time.
 *
 * @returns {'staging' | 'production'} 'production' for production builds; 'staging' for
 *   staging and local development.
 */
export const getFlagEnvironment = () => (import.meta.env.VITE_ENV === 'production' ? 'production' : 'staging')

/**
 * resolveFlagName - Builds the environment-suffixed PostHog flag key for a registered base name.
 *
 * @param {string} baseName - A value from FEATURE_FLAGS, e.g. 'use_webstore_quote_attachments'.
 * @returns {string} The flag key as configured in PostHog, e.g. 'use_webstore_quote_attachments_staging'.
 */
export const resolveFlagName = (baseName) => `${baseName}_${getFlagEnvironment()}`
